import React, { useEffect, useState } from 'react';
import Image from 'next/image';
import { useSelector } from 'react-redux';

/**
 * Hero — Graha Prime template "image-infinite-bg" pattern.
 *
 * Олон cover зураг 5 секунд тутамд slow crossfade хийнэ.
 * Админаас 1-5 ширхэг cover нэмэх боломжтой.
 *
 * @param {Array} covers — cover жагсаалт (getActiveCovers-аас)
 */
export default function Hero({ covers = [] }) {
    const lang = useSelector((s) => s.system.lang || 'mn');
    const companyInfo = useSelector((s) => s.system.companyInfo || {});

    // Cover-гүй бол placeholder ашиглах
    const images = covers.length > 0
        ? covers.map((c) => c.imageUrl).filter(Boolean)
        : ['/placeholders/hero-default.svg'];

    const [activeIndex, setActiveIndex] = useState(0);

    // Smooth crossfade slideshow
    useEffect(() => {
        if (images.length <= 1) return;
        const interval = setInterval(() => {
            setActiveIndex((prev) => (prev + 1) % images.length);
        }, 6000); // 6 секунд тутам солих (3с fade + 3с тогтмол)
        return () => clearInterval(interval);
    }, [images.length]);

    const title =
        lang === 'en'
            ? companyInfo.hero_title_en || companyInfo.hero_title || "Building Mongolia's Energy Future"
            : companyInfo.hero_title || 'Эрчим хүчний эрх чөлөөг эх орондоо';

    // Сүүлийн хоёр үгийг (ж: "эх орондоо") нэг мөрөнд барина — хоорондох зайг
    // тасрахгүй зай (NBSP) болгож, мөр дундуур таслахаас сэргийлнэ.
    const displayTitle = (title || '').trim().replace(/\s+(\S+)$/, '\u00A0$1');

    return (
        <section className="relative h-screen min-h-[600px] max-h-[1080px] flex items-center bg-brand-dark overflow-hidden">
            {/* ── Background smooth crossfade slideshow ── */}
            {images.map((src, i) => (
                <div
                    key={i}
                    className="absolute inset-0"
                    style={{
                        opacity: i === activeIndex ? 1 : 0,
                        transition: 'opacity 3s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                >
                    <Image
                        src={src}
                        alt=""
                        fill
                        priority={i === 0}
                        className="object-cover"
                        sizes="100vw"
                    />
                </div>
            ))}

            {/* Dark overlay + grid pattern */}
            <div className="absolute inset-0 bg-gradient-to-r from-brand-dark/60 via-brand-dark/25 to-transparent" />
            <div className="absolute inset-0 bg-grid-pattern opacity-30" />

            {/* ── Content ── */}
            <div className="relative z-10 max-w-[1440px] mx-auto px-4 lg:px-12 w-full">
                <div className="max-w-2xl flex flex-col gap-5 lg:pl-8">
                    {/* Heading — эхэнд гарч ирэх */}
                    <h1
                        className="font-heading uppercase font-bold leading-[1.05] tracking-tight text-white animate-fade-in-slow order-2"
                        style={{
                            fontSize: 'clamp(2.5rem, 5.5vw, 5rem)',
                            letterSpacing: '0.01em',
                            animationDelay: '0.1s',
                        }}
                    >
                        {displayTitle}
                    </h1>

                    {/* Eyebrow — heading-ийн дараа */}
                    <span
                        className="text-brand-yellow text-sm font-bold uppercase tracking-[0.25em] animate-fade-in-slow order-1"
                        style={{ animationDelay: '1.4s' }}
                    >
                        {lang === 'en'
                            ? 'Mongol Refinery State Owned LLC'
                            : 'Монгол Газрын Тос Боловсруулах Үйлдвэр ТӨХХК'}
                    </span>
                </div>
            </div>

            {/* ── Slide indicators (template-д адил) ── */}
            {images.length > 1 && (
                <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex gap-2">
                    {images.map((_, i) => (
                        <button
                            key={i}
                            onClick={() => setActiveIndex(i)}
                            aria-label={`Slide ${i + 1}`}
                            className={`h-1 transition-all duration-300 ${
                                i === activeIndex
                                    ? 'w-12 bg-brand-yellow'
                                    : 'w-6 bg-white/30 hover:bg-white/50'
                            }`}
                        />
                    ))}
                </div>
            )}
        </section>
    );
}